/**
 * AP Invoices Demo — auto-runs Nami's build turn on the workflow canvas.
 *
 * THROWAWAY DEMO CODE. The create-workflow "describe the workflow" prompt sets a
 * pending description and navigates here. Once the canvas has registered its
 * agent module (so the chat panel can actually render), this hook opens the
 * panel and streams a scripted Nami turn — the user's message, then Nami's reply
 * — and builds the pipeline as the reply finishes, so it reads as the agent
 * doing the work. Renders nothing; mounted from `ApWorkflowAgentModule`. Delete
 * with src/demo/ap.
 */

import { useEffect, useRef } from 'react';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState } from '@/context/agent-state.context';
import { AP_BUILD_WORKFLOW_NARRATION } from '../agent/script';
import {
  consumePendingApWorkflowDescribe,
  startApWorkflowBuild,
} from './build-actions';

export function useApWorkflowDescribeAutostart(): void {
  const { moduleConfig } = useAgentContainer();
  const hasModule = moduleConfig !== null;
  const { open } = useAgentPanel();
  const { runDemoScriptedTurn } = useAgentState();
  const started = useRef(false);

  useEffect(() => {
    // Wait until the canvas module is registered — opening the panel before then
    // trips FloatingAgentPanel's "no module → close" guard, and the chat has
    // nowhere to render. Consume only once we can actually show the turn.
    if (started.current || !hasModule) return;
    const description = consumePendingApWorkflowDescribe();
    if (!description) return;
    started.current = true;
    open();
    void runDemoScriptedTurn(description, AP_BUILD_WORKFLOW_NARRATION, () => {
      startApWorkflowBuild();
    });
  }, [hasModule, open, runDemoScriptedTurn]);
}
